/**
 * Implementación Dexie del repo de Configuración de IA.
 */

import type { ConfiguracionIARepo } from "../../repos/ConfiguracionIARepo";
import type { ConfiguracionIA } from "@app-types/entities";
import type { GerenciaCodigo } from "@constants/gerencias";
import { OptimisticLockError } from "../../repos/BaseRepo";
import { localDb } from "./db";

export class DexieConfiguracionIARepo implements ConfiguracionIARepo {
  constructor(private tenantId: string) {}

  async get(gerenciaId?: GerenciaCodigo): Promise<ConfiguracionIA | null> {
    const filas = await localDb.configuracionesIA
      .where("tenantId")
      .equals(this.tenantId)
      .toArray();
    if (gerenciaId) {
      // Singleton per-gerencia: busca la fila de esa gerencia. Si no
      // existe, devuelve null para que el upsert la cree.
      return filas.find((f) => f.gerenciaId === gerenciaId) ?? null;
    }
    return filas[0] ?? null;
  }

  async upsert(
    data: Omit<
      ConfiguracionIA,
      "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy"
    >,
    expectedVersion?: number,
  ): Promise<ConfiguracionIA> {
    // Si data trae gerenciaId, opera sobre el singleton per-gerencia.
    const actual = await this.get(data.gerenciaId);
    const now = new Date().toISOString();
    if (!actual) {
      const nuevo: ConfiguracionIA = {
        ...data,
        id: crypto.randomUUID(),
        tenantId: this.tenantId,
        version: 1,
        createdAt: now,
        updatedAt: now,
        createdBy: "admin",
        updatedBy: "admin",
      };
      await localDb.configuracionesIA.add(nuevo);
      return nuevo;
    }
    if (expectedVersion !== undefined && actual.version !== expectedVersion) {
      throw new OptimisticLockError(actual.id, expectedVersion, actual.version);
    }
    const actualizado: ConfiguracionIA = {
      ...actual,
      ...data,
      version: actual.version + 1,
      updatedAt: now,
    };
    await localDb.configuracionesIA.put(actualizado);
    return actualizado;
  }
}
